import AppButton from '@nxsys/components/AppButton';
import {
  defaultHolidayPayCalcOptions,
  holidayPaymentMethodOptions,
} from '@nxsys/constants/AppConstants';
import { YesNoNAEnum } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PayrollSettingForm, {
  PayrollSettingFormRef,
  PayrollSettingFormValues,
} from './components/PayrollSettingForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const PayrollSettingTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const companyId = params?.companyId ?? null;
  const formRef = useRef<PayrollSettingFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: PayrollSettingFormValues) => {
      if (!companyId) return;
      setIsUpdating(true);

      try {
        const params: UpdateCompanyByIdParams = {
          timesheetApprovalNeeded:
            (values.timesheetApprovalNeeded as string) ??
            YesNoNAEnum.NOT_AVAILABLE,
          payrollApproveNeeded:
            (values.payrollApproveNeeded as string) ??
            YesNoNAEnum.NOT_AVAILABLE,
          listEmprApprovers: values.listEmprApprovers
            ? values.listEmprApprovers.map((item) => item.value)
            : [],
          enableHolidayPay: values.enableHolidayPay ?? false,
          payeHolidayPayRate: values.payeHolidayPayRate
            ? Number(values.payeHolidayPayRate)
            : null,
          holidayPayMethod: values.holidayPayMethod?.value ?? null,
          holidayPayCalculationRule:
            values.holidayPayCalculationRule?.value ?? null,
          includeInTimesheetRate: values.includeInTimesheetRate ?? null,
        };

        await updateData(params);
      } catch (error) {
        console.error(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [companyId, updateData, messages],
  );

  return (
    <div className="PayrollSettingTab">
      {data && (
        <>
          <PayrollSettingForm
            formRef={formRef}
            onSubmit={handleSubmit}
            companyId={companyId ?? undefined}
            defaultValues={{
              timesheetApprovalNeeded:
                (data?.timesheetApprovalNeeded as YesNoNAEnum) ?? null,
              listEmprApprovers: data?.listEmprApprovers
                ? data.listEmprApprovers.map((item: any) => ({
                    label: `${item?.forename || ''} ${item?.surname}`,
                    value: item.id,
                  }))
                : null,
              payrollApproveNeeded:
                (data?.payrollApproveNeeded as YesNoNAEnum) ?? null,
              enableHolidayPay: data?.enableHolidayPay ?? false,
              payeHolidayPayRate: data?.payeHolidayPayRate?.toString() ?? '',
              holidayPayMethod:
                holidayPaymentMethodOptions.find(
                  (item) => item.value === data?.holidayPayMethod,
                ) ?? null,
              holidayPayCalculationRule:
                defaultHolidayPayCalcOptions.find(
                  (item) => item.value === data?.holidayPayCalculationRule,
                ) ?? null,
              includeInTimesheetRate: data?.includeInTimesheetRate ?? null,
            }}
          />
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => formRef.current?.submit()}
              loading={isUpdating}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PayrollSettingTab;
